import React from 'react'

const img = (name: string) => `${process.env.PUBLIC_URL}/images/${name}`

interface Feature {
  eyebrow: string
  title: string
  body: string
  image: string
}

const FEATURES: Feature[] = [
  {
    eyebrow: 'A curated library',
    title: 'Classics, ready to read and hear',
    body: 'Dozens of hand-picked works of literature — Austen, Dostoevsky, Dickens, Homer and more — sourced from Project Gutenberg and in the public domain. Browse in light, dark, or sepia.',
    image: 'heard-library-dark.jpg',
  },
  {
    eyebrow: 'A reader that disappears',
    title: 'Beautiful, distraction-free pages',
    body: 'Clean typography with adjustable font size and serif, sans, or mono styles. Tap the edges to turn pages, tap the center for full screen. Your place is always saved.',
    image: 'heard-alice.jpg',
  },
  {
    eyebrow: 'On-device narration',
    title: 'A neural voice, generated right on your phone',
    body: 'Heard synthesizes natural speech locally — no streaming, no servers, no account. The first time a page is read it’s generated in seconds, then it just plays. Works on a plane, in a tunnel, anywhere.',
    image: 'heard-alice-generating.jpg',
  },
  {
    eyebrow: 'Press play, keep listening',
    title: 'Pages turn themselves as you listen',
    body: 'Narration flows from page to page automatically — set it going and follow along, or simply listen as one page leads into the next.',
    image: 'heard-alice-playing.jpg',
  },
  {
    eyebrow: 'Pocket-safe listening',
    title: 'Lock the screen, keep listening',
    body: 'Heard currently can\'t generate audio in the background. But you can tap Lock Screen while a book is playing and the display dims and ignores taps — so you can slip your phone in your pocket without pausing the story or turning a page by accident. Swipe down to unlock.',
    image: 'heard-lockscreen.png',
  },
  {
    eyebrow: 'Faithful to the book',
    title: 'Original illustrations, preserved',
    body: 'Where a classic edition includes artwork, Heard keeps it — so illustrated favorites look the way they were meant to.',
    image: 'heard-illustration.jpg',
  },
  {
    eyebrow: 'Find your next read',
    title: 'Search by title or author',
    body: 'Jump straight to the book you want. The library filters as you type, so the whole shelf is a tap away.',
    image: 'heard-search-odyssey.jpg',
  },
]

const PRO = [
  'Every neural voice — choose the narrator you like best',
  'Import your own EPUBs and listen to any book you own',
  'Support independent, privacy-first development',
]

export default function Home() {
  return (
    <>
      <section className="hero">
        <div className="container">
          <img className="hero-icon" src={img('heard-icon.png')} alt="Heard app icon" />
          <h1>Heard</h1>
          <p className="tag">Listen to the classics. On-device. Private.</p>
          <p className="sub">
            A library of great books that reads itself aloud — with a natural neural voice
            generated entirely on your phone, no internet needed. 56 classic books included free,
            each ready to read and listen to.
          </p>
          <div className="hero-cta">
            <a
              className="btn"
              href="/"
              onClick={(e) => {
                e.preventDefault()
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
              }}
            >
              See how it works
            </a>
            <a className="btn btn-ghost" href="https://apps.apple.com/" target="_blank" rel="noreferrer">
              Download on the App Store
            </a>
          </div>
          <p className="byline">From Alana .sh</p>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          {FEATURES.map((f, i) => (
            <div key={f.title} className={`row${i % 2 === 1 ? ' reverse' : ''}`}>
              <div className="row-text">
                <div className="eyebrow">{f.eyebrow}</div>
                <h2>{f.title}</h2>
                <p>{f.body}</p>
              </div>
              <div className="row-media">
                <img className="phone" src={img(f.image)} alt={f.title} loading="lazy" />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pro">
        <div className="container">
          <h2>Heard Pro</h2>
          <p className="sub">Heard is free to read the whole library with one built-in voice. Go Pro to unlock more:</p>
          <ul className="pro-list">
            {PRO.map((p) => (
              <li key={p}>
                <span className="check">✓</span>
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <a className="btn" href="https://apps.apple.com/" target="_blank" rel="noreferrer">
            Get Heard
          </a>
          <p className="free-note">Free to read &amp; listen with the default voice.</p>
        </div>
      </section>
    </>
  )
}
